import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const AuthCallback = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { googleAuth } = useAuth();
  const hasProcessed = useRef(false);
  const [processing, setProcessing] = useState(true);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      // Use window.location.hash directly for better mobile compatibility
      // and add a small delay to ensure the URL is fully loaded
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Try hash first, then query params as fallback
      const hash = window.location.hash;
      const search = window.location.search;
      let sessionId = null;
      
      if (hash) {
        const hashParams = new URLSearchParams(hash.replace('#', ''));
        sessionId = hashParams.get('session_id');
      }
      if (!sessionId && search) {
        const queryParams = new URLSearchParams(search);
        sessionId = queryParams.get('session_id');
      }

      console.log('AuthCallback - Hash:', hash);
      console.log('AuthCallback - Search:', search);
      console.log('AuthCallback - Session ID:', sessionId ? 'found' : 'not found');

      if (!sessionId) {
        console.error('AuthCallback - No session_id in URL');
        setProcessing(false);
        toast.error(t('errors.authFailed'));
        // Delay navigation to ensure state is set
        setTimeout(() => navigate('/login', { replace: true }), 100);
        return;
      }

      try {
        console.log('AuthCallback - Calling googleAuth API...');
        const response = await googleAuth(sessionId);
        console.log('AuthCallback - Success:', response.user?.email);
        
        if (response.user.is_admin) {
          toast.success(`${t('auth.welcomeBackToast')} ${response.user.name}! ${t('auth.adminPrivileges')}`);
        } else {
          toast.success(`${t('auth.welcomeBackToast')} ${response.user.name}!`);
        }
        // Use replace to prevent back button issues
        navigate('/', { replace: true });
      } catch (error) {
        console.error("Auth callback error:", error);
        setProcessing(false);
        toast.error(error.response?.data?.detail || t('errors.authFailed'));
        setTimeout(() => navigate('/login', { replace: true }), 100);
      }
    };

    processAuth();
  }, [navigate, googleAuth, t]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="text-center">
        <Loader2 className="w-12 h-12 animate-spin text-[#0F766E] mx-auto mb-4" />
        <p className="text-slate-600">{t('auth.completingSignIn')}</p>
      </div>
    </div>
  );
};

export default AuthCallback;
